import "tailwindcss/dist/base.css";
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import logo from "../assets/images/Uclusion_Wordmark_Color.svg";
import useAnimatedNavToggler from "./helpers/useAnimatedNavToggler";
import { Close, Menu } from "@material-ui/icons";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { useHistory } from "react-router-dom";

const BLOG_URL = "https://blog.uclusion.com";
const DOCS_URL = "https://documentation.uclusion.com";

const Header = tw.div`
z-10
flex justify-between mt-0
bg-white
w-full
top-0 left-0
fixed
bg-green-500
`;

export const NavLinks = tw.div`flex items-center justify-start`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLink = tw.a`
  lg:text-sm lg:mx-6 
  font-sans tracking-wide transition duration-300
  border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

const PrimaryButton = tw.a`px-1 py-1 text-primary-500 border-3 border-primary-500 border-solid hover:bg-primary-500 hover:text-gray-100 focus:outline-none bg-white font-sans font-bold inline-block mr-2 rounded-xl`;


export const LogoLink = styled(NavLink)`
  ${tw`inline-block font-black border-b-0 -mt-2 text-2xl! ml-0!`};
  img {
    ${tw`w-10`}
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between pr-2`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

const IframeContainer = styled.div`
  position: fixed;
  top: ${props => props.headerHeight || '56px'};
  display: ${props => props.display || 'block'};
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  background: white;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

export const scrollToHash = (hash) => {
  const id = hash.replace('#', '');
  const settle = (attempts) => {
    const el = document.getElementById(id);
    if (!el) {
      if (attempts > 0) {
        setTimeout(() => settle(attempts - 1), 100);
      }
      return;
    }
    let lastTop = el.getBoundingClientRect().top;
    const waitForStable = (remaining) => {
      setTimeout(() => {
        const currentTop = el.getBoundingClientRect().top;
        if (currentTop === lastTop || remaining <= 0) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else {
          lastTop = currentTop;
          waitForStable(remaining - 1);
        }
      }, 150);
    };
    waitForStable(10);
  };
  settle(20);
};

const SiteHeader = () => {
  const history = useHistory();
  const theme = useTheme();
  const midLayout = useMediaQuery(theme.breakpoints.down('md'));
  const [iframeUrl, setIframeUrl] = useState(null);

  // Hide outer scrollbar and clear URL hash when iframe is displayed
  useEffect(() => {
    if (iframeUrl) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [iframeUrl]);
  
  /*
   * This header component accepts an optionals "links" prop that specifies the links to render in the navbar.
   * This links props should be an array of "NavLinks" components which is exported from this file.
   * Each "NavLinks" component can contain any amount of "NavLink" component, also exported from this file.
   * This allows this Header to be multi column.
   * So If you pass only a single item in the array with only one NavLinks component as root, you will get 2 column header.
   * Left part will be LogoLink, and the right part will be the the NavLinks component you
   * supplied.
   * Similarly if you pass 2 items in the links array, then you will get 3 columns, the left will be "LogoLink", the center will be the first "NavLinks" component in the array and the right will be the second "NavLinks" component in the links array.
   * You can also choose to directly modify the links here by not passing any links from the parent component and
   * changing the defaultLinks variable below below.
   * If you manipulate links here, all the styling on the links is already done for you. If you pass links yourself though, you are responsible for styling the links or use the helper styled components that are defined here (NavLink)
   */

  // Handler for iframe links (Blog, Documentation)
  const handleIframeLink = (url) => (event) => {
    event.preventDefault();
    // Remove hash from URL if present
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    setIframeUrl(url);
  };

  const handleRegularLink = () => {
    setIframeUrl(null);
  };

  const handleHashLink = (hash) => (event) => {
    event.preventDefault();
    setIframeUrl(null);
    if (window.location.pathname !== '/') {
      history.push('/' + hash);
      scrollToHash(hash);
    } else {
      scrollToHash(hash);
    }
  };

  const defaultLinks = [
    <NavLinks key="default">
      <NavLink href="https://production.uclusion.com" tw="lg:ml-12!" onClick={handleRegularLink}>
        Login
      </NavLink>
      <PrimaryButton style={{marginRight: '1rem'}} href="https://production.uclusion.com/#signup" onClick={handleRegularLink}>
        Sign Up
      </PrimaryButton>
    </NavLinks>
  ];

  const logoLinks = [
      <NavLinks key="logo">
        <LogoLink href="/" onClick={handleRegularLink}>
          <img src={logo} alt="logo" style={{width: '150px'}} />
        </LogoLink>
       </NavLinks>
  ]

  const siteLinks = [
      <NavLinks key="site">
        <NavLink href="#blog" onClick={handleIframeLink(BLOG_URL)} style={iframeUrl === BLOG_URL ? {borderBottomColor: '#6415ff', color: '#6415ff'} : {}}>Blog</NavLink>
        <NavLink href="/#pricing" onClick={handleHashLink('#pricing')}>Pricing</NavLink>
        <NavLink href="#docs" onClick={handleIframeLink(DOCS_URL)} style={iframeUrl === DOCS_URL ? {borderBottomColor: '#6415ff', color: '#6415ff'} : {}}>Documentation</NavLink>
        <NavLink href="/research" onClick={handleRegularLink}>Uclusion for researchers</NavLink>
        <NavLink href="/aboutus" onClick={handleRegularLink}>About Us</NavLink>
      </NavLinks>
  ];

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap['lg'];

  function scrollLess(event) {
    event.preventDefault();
    setIframeUrl(null);
    toggleNavbar();
    if (window.location.pathname !== '/') {
      history.push('/#pricing');
      scrollToHash('#pricing');
    } else {
      const element = document.getElementById("pricing");
      window.scrollTo({
        behavior: 'smooth',
        top: element.getBoundingClientRect().top - 70,
      });
    }
  }

  const handleMobileIframeLink = (url) => (event) => {
    event.preventDefault();
    toggleNavbar();
    setIframeUrl(url);
  };

  const handleMobileRegularLink = () => {
    setIframeUrl(null);
  };

  const siteLinksMobile = [
    <NavLinks key="site-mobile">
      <NavLink href="#blog" onClick={handleMobileIframeLink(BLOG_URL)} style={iframeUrl === BLOG_URL ? {borderBottomColor: '#6415ff', color: '#6415ff'} : {}}>Blog</NavLink>
      <NavLink href="/#pricing" onClick={scrollLess}>Pricing</NavLink>
      <NavLink href="#docs" onClick={handleMobileIframeLink(DOCS_URL)} style={iframeUrl === DOCS_URL ? {borderBottomColor: '#6415ff', color: '#6415ff'} : {}}>Documentation</NavLink>
      <NavLink href="/research" onClick={handleMobileRegularLink}>Uclusion for researchers</NavLink>
      <NavLink href="/aboutus" onClick={handleMobileRegularLink}>About Us</NavLink>
    </NavLinks>
  ];


  const headerHeight = midLayout ? '2.5rem' : '56px';

  return (
    <>
      <Header style={{maxHeight: midLayout ? '2.5rem' : undefined}}>
        <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
          {logoLinks}
          {siteLinks}
          {defaultLinks}
        </DesktopNavLinks>

        <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
          {logoLinks}
          <MobileNavLinks initial={{ x: "150%", display: "none" }} animate={animation} css={collapseBreakpointCss.mobileNavLinks}>
            {siteLinksMobile}
            {defaultLinks}
          </MobileNavLinks>
          <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"} 
          style={{marginRight: showNavLinks ? '1rem' : undefined, marginTop: showNavLinks ? '2rem' : undefined}}>
            {showNavLinks ? <Close /> : <Menu />}
          </NavToggle>
        </MobileNavLinksContainer>
      </Header>
      
      <IframeContainer headerHeight={headerHeight} display={iframeUrl === BLOG_URL ? 'block' : 'none'}>
        <iframe 
          src={BLOG_URL}
          title="Embedded Content"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </IframeContainer>
      
      <IframeContainer headerHeight={headerHeight} display={iframeUrl === DOCS_URL ? 'block' : 'none'}>
        <iframe 
          src={DOCS_URL}
          title="Embedded Content"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </IframeContainer>


    </>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  }
};


export default SiteHeader;